import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, ShieldCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/site/Navbar";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { UsageRing, IntroList } from "@/components/member/IntroList";
import { AddonForm } from "@/components/member/AddonForm";
import { ProfileForm } from "@/components/member/ProfileForm";
import { AnnouncementList } from "@/components/AnnouncementList";
import { useAuth } from "@/context/AuthContext";

const MemberNews = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/member/announcements").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  return items && <AnnouncementList items={items} />;
};
import { api, formatErr, money } from "@/lib/api";

const Welcome = ({ d }) => (
  <div className="relative overflow-hidden rounded-[2rem] bg-[#0A170F] text-white p-8 sm:p-10 grid gap-8 md:grid-cols-[1fr_auto] items-center grain" data-testid="member-welcome-card">
    <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-purple-600/40 blur-3xl" />
    <div className="absolute -bottom-24 left-10 h-64 w-64 rounded-full bg-emerald-500/30 blur-3xl" />
    <div className="relative">
      <span className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#0A1F13] px-3 py-1 text-xs font-extrabold" data-testid="member-tier-badge"><ShieldCheck className="h-3.5 w-3.5" />{d.tier.name} Member</span>
      <h1 className="mt-4 text-3xl sm:text-5xl font-extrabold tracking-tight">Welcome, {d.name.split(" ")[0]}.</h1>
      <p className="mt-3 text-white/75 max-w-lg">You're in. Here's everything about your farm introductions in one place.</p>
      <div className="mt-6 flex flex-wrap gap-6 text-sm">
        <div><p className="font-display text-2xl font-bold" data-testid="member-intros-used">{d.used}</p><p className="text-white/60">used</p></div>
        <div><p className="font-display text-2xl font-bold" data-testid="member-intros-limit">{d.limit}{d.tier.key === "platinum" ? "+" : ""}</p><p className="text-white/60">included</p></div>
        <div><p className="font-display text-2xl font-bold">{money(d.tier.price)}</p><p className="text-white/60">one-time</p></div>
      </div>
    </div>
    <div className="relative justify-self-center"><UsageRing used={d.used} limit={d.limit} /></div>
  </div>
);

export default function MemberPortal() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/member/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e))), []);
  useEffect(() => {
    load();
  }, [load]);
  const out = async () => {
    await logout();
    nav("/login");
  };
  return (
    <div className="min-h-screen" data-testid="member-portal-page">
      <header className="glass border-b border-border sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex gap-2"><ThemeToggle /><button onClick={out} className="h-10 px-4 rounded-full border border-border text-sm font-semibold inline-flex items-center gap-2 hover:bg-secondary" data-testid="member-logout-button"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Log out</span></button></div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {!d ? <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : (
          <>
            <Welcome d={d} />
            <Tabs defaultValue="intros" className="mt-10">
              <TabsList className="rounded-full">
                <TabsTrigger value="intros" className="rounded-full" data-testid="member-tab-intros">Introductions</TabsTrigger>
                <TabsTrigger value="addons" className="rounded-full" data-testid="member-tab-addons">Add-on services</TabsTrigger>
                <TabsTrigger value="profile" className="rounded-full" data-testid="member-tab-profile">Profile</TabsTrigger>
                <TabsTrigger value="news" className="rounded-full" data-testid="member-tab-announcements">Announcements</TabsTrigger>
              </TabsList>
              <TabsContent value="intros" className="mt-6"><IntroList intros={d.introductions} /></TabsContent>
              <TabsContent value="addons" className="mt-6"><AddonForm /></TabsContent>
              <TabsContent value="profile" className="mt-6"><ProfileForm profile={d.profile} onSaved={load} /></TabsContent>
              <TabsContent value="news" className="mt-6"><MemberNews /></TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
